import { useCallback } from 'react';
import { useCollectionsStore } from '../../store/collectionsStore';
import { setActiveTabType, useTabsStore, setRequestTabSyncRevision } from '../../store/tabsStore';
import { useVariablesStore } from '../../store/variablesStore';
import {
  setActiveTab,
  setCollectionRunnerFolderId,
  setCollectionRunnerAutoRun,
  setCollectionRunnerTarget,
  setShowCollectionRunnerModal,
} from '../../store/uiModalStore';
import { currentRequestFacts } from '../../services/ai/requestFacts';
import { normalizeSingleBraceVariables } from '../../utils/localIds';
import { resolvedRequestName } from '../../utils/deriveRequestNameFromUrl';
import { defaultRequestAuth } from '../../utils/requestAuth';
import { flattenCollectionRequests, runCollectionRequests, formatCollectionRunResult } from '../../utils/collectionRunner';
import { sendRequestViaTauri } from '../../utils/tauri';
import { withUserNetwork } from '../../utils/requestNetworkOptions';
import { runnerSendOptions } from '../../services/runnerSendOptions';
import { headersFromKvRows } from './aiRequestHelpers';

/** Assistant tools that send: the current editor request, execute a request, run a collection. */
export function useAiRequestExecution({
  selectRequest,
  createNewRequestTab,
  requestBuilderSendRef,
  requestBuilderLiveStateRef,
  collectionRunnerAiResolveRef,
}) {
  const waitForRequestBuilderSend = useCallback(async (timeoutMs = 5000) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (requestBuilderSendRef.current) return requestBuilderSendRef.current;
      await new Promise((r) => setTimeout(r, 50));
    }
    return null;
  }, [requestBuilderSendRef]);

  const ensureRequestBuilderFocused = useCallback(() => {
    setActiveTab('collections');
    setActiveTabType('request');
  }, []);

  const flushAndWaitForSendHandler = useCallback(async () => {
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    const sendFn = await waitForRequestBuilderSend();
    if (!sendFn) throw new Error('Request Builder is not ready');
    return sendFn;
  }, [waitForRequestBuilderSend]);

  const interactiveSendCurrentTab = useCallback(async () => {
    ensureRequestBuilderFocused();
    const sendFn = await flushAndWaitForSendHandler();
    const raw = await sendFn();
    if (!raw) throw new Error('Request was cancelled or URL is empty');
    return raw;
  }, [ensureRequestBuilderFocused, flushAndWaitForSendHandler]);

  const getCurrentEditorRequest = useCallback(() => {
    if (useTabsStore.getState().activeTabType !== 'request') {
      return { error: 'No active request tab' };
    }
    const { requestTabs, activeRequestTabId } = useTabsStore.getState();
    const tab = requestTabs.find((t) => t.id === activeRequestTabId);
    if (!tab) return { error: 'No active request tab' };
    const live = requestBuilderLiveStateRef.current?.() ?? null;
    const source = live || tab;
    return {
      request: {
        tab_id: tab.id,
        is_new_request: Boolean(tab.isNewRequest),
        id: tab.originalId ?? null,
        saved_request_id: tab.originalId ?? null,
        collection_id: tab.collectionId ?? null,
        method: source.method || tab.method || 'GET',
        url: source.url || tab.url || '',
        name: source.name || tab.name || null,
        has_unsaved_changes: Boolean(tab.hasUnsavedChanges),
        // type / effective auth / gRPC-WS-SSE fields, names only.
        ...currentRequestFacts(tab, live, useCollectionsStore.getState().collections),
      },
    };
  }, [requestBuilderLiveStateRef]);

  const aiExecuteRequest = useCallback(async (toolInput, { interactive } = {}) => {
    if (toolInput.use_current_tab) {
      const { requestTabs, activeRequestTabId } = useTabsStore.getState();
      const tab = requestTabs.find((t) => t.id === activeRequestTabId);
      if (!tab) throw new Error('No active request tab');

      if (!interactive) {
        const live = requestBuilderLiveStateRef.current?.() ?? tab;
        return sendRequestViaTauri(withUserNetwork({
          method: live.method || tab.method || 'GET',
          url: live.url || tab.url,
          headers: headersFromKvRows(live.headers || tab.headers),
          body: live.body || tab.body || null,
        }));
      }
      return interactiveSendCurrentTab();
    }

    if (!interactive) {
      return sendRequestViaTauri(withUserNetwork({
        method: toolInput.method || 'GET',
        url: toolInput.url,
        headers: toolInput.headers || {},
        body: toolInput.body || null,
      }));
    }

    ensureRequestBuilderFocused();

    if (toolInput.request_id) {
      const cols = useCollectionsStore.getState().collections;
      let found = null;
      for (const col of cols || []) {
        const req = (col.requests || []).find((r) => String(r.id) === String(toolInput.request_id));
        if (req) { found = req; break; }
      }
      if (!found) throw new Error('Request not found');
      await selectRequest(found);
    } else {
      const headerRows = toolInput.headers && typeof toolInput.headers === 'object'
        ? Object.entries(toolInput.headers)
          .filter(([key]) => key)
          .map(([key, value]) => ({
            key: normalizeSingleBraceVariables(key),
            value: normalizeSingleBraceVariables(String(value ?? '')),
            enabled: true,
          }))
        : [];
      createNewRequestTab({
        name: resolvedRequestName(toolInput.name, toolInput.url),
        method: toolInput.method || 'GET',
        url: normalizeSingleBraceVariables(toolInput.url || ''),
        headers: headerRows.length > 0 ? headerRows : [{ key: '', value: '', enabled: true }],
        params: [{ key: '', value: '', enabled: true }],
        cookies: [{ key: '', value: '', enabled: true }],
        body: toolInput.body || '',
        bodyType: toolInput.body ? 'json' : 'none',
        auth: defaultRequestAuth(),
        isNewRequest: true,
      });
      setRequestTabSyncRevision((n) => n + 1);
    }

    return interactiveSendCurrentTab();
  }, [createNewRequestTab, selectRequest, ensureRequestBuilderFocused, interactiveSendCurrentTab, requestBuilderLiveStateRef]);

  const aiRunCollection = useCallback(async (input, { interactive } = {}) => {
    const cols = useCollectionsStore.getState().collections;
    let collection = null;

    if (input.collection_id) {
      collection = cols.find((c) => String(c.id) === String(input.collection_id));
    } else if (input.collection_name) {
      const target = String(input.collection_name).trim().toLowerCase();
      collection = cols.find((c) => String(c.name || '').trim().toLowerCase() === target);
    }

    if (!input.collection_id && !input.collection_name) {
      return { error: 'collection_id or collection_name is required' };
    }
    if (!collection) return { error: 'Collection not found' };

    const requests = flattenCollectionRequests(collection, { folderId: input.folder_id ?? null });
    if (requests.length === 0) return { error: 'No requests in collection' };

    if (interactive) {
      return new Promise((resolve) => {
        collectionRunnerAiResolveRef.current = resolve;
        setCollectionRunnerFolderId(input.folder_id ?? null);
        setCollectionRunnerAutoRun(true);
        setCollectionRunnerTarget(collection);
        setShowCollectionRunnerModal(true);
      });
    }

    const vars = useVariablesStore.getState();
    const results = await runCollectionRequests({
      collection,
      selectedEnvironment: vars.selectedEnvironment,
      collectionVariables: vars.collectionVariablesById[collection.id] || [],
      workspaceVariables: vars.workspaceVariables,
      globalVariables: vars.globalVariables,
      folderId: input.folder_id ?? null,
      ...runnerSendOptions(),
    });

    return formatCollectionRunResult(collection, results, { interactive: false });
  }, [collectionRunnerAiResolveRef]);

  return { getCurrentEditorRequest, aiExecuteRequest, aiRunCollection };
}
